import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import {
  FullWidthSection,
  JourneyCard,
  JourneyMap,
  Loader,
  PageFooter,
  PageWrapper,
} from 'src/components'
import { MapProvider } from 'src/contexts/journey-map.context'
import { useAccount, useImpacts, useProductJourney } from 'src/hooks'
import { LeftDivider, TopDivider } from '../product/product.styled'
import { LayoutContainer } from 'src/components/layout-container'
import { PATHS } from 'src/constants'
import { Typography, useTheme } from '@mui/material'
import { FlaskIcon } from 'src/assets/icons/v2/FlaskIcon'
import { getBaseUrl } from 'src/utils/base-url.util'
import { JOURNEY_PAGE_TEMPLATE_ID } from 'src/templates/constants'
import { FairtradeJourneyTemplate } from 'src/templates/fairtrade/journey'

export default function JourneyPage() {
  const [t, i18n] = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { productId, simpleProductId } = useParams()
  const { data, isLoading } = useProductJourney(productId, i18n.language, simpleProductId)
  const theme = useTheme()

  const { isLoading: isImpactsLoading, isImpacts, product } = useImpacts()
  const { data: account, isLoading: isAccountLoading } = useAccount(
    product?.account_id,
    i18n.language,
  )

  const ref = useRef<HTMLSpanElement | null>(null)
  const topRef = useRef<HTMLSpanElement | null>(null)

  const journey = data?.product?.journey
  const steps = journey?.steps || []

  useEffect(() => {
    if (data && steps && steps.length === 0 && !isLoading) {
      if (location.state?.to) {
        navigate(location.pathname.replace(PATHS.JOURNEY, PATHS.IMPACTS), {
          state: {
            from: location.pathname.replace(`/${PATHS.JOURNEY}`, ''),
            to: `${location.pathname}/${PATHS.IMPACTS}`,
          },
        })

        return
      }
      navigate(location.pathname.replace(`/${PATHS.JOURNEY}`, ''))
      return
    }
  }, [steps, isLoading])

  if (!steps || isLoading || isAccountLoading || !account) {
    return <Loader />
  }

  if (!product || isImpactsLoading) {
    return <Loader />
  }

  // --- SEO (Helmet) ---
  const base = getBaseUrl()
  const canonical = `${base}${window.location.pathname}`

  const holder = account?.account_holder ?? 'the brand'
  const journeyTitle = `Trackgood Journey ${product?.name} Journey`
  const journeyDesc = `Explore the journey of ${product.name} from ${holder} on Trackgood Journey.`

  const isFairtradeTemplate =
    account?.journey_page_template_id === JOURNEY_PAGE_TEMPLATE_ID.FAIRTRADE

  return (
    <LayoutContainer>
      <Helmet>
        <title id="index__pages-journey__title__l72">{journeyTitle}</title>
        <meta id="index__pages-journey__meta__l73" name="description" content={journeyDesc} />
        <link id="index__pages-journey__link__l74" rel="canonical" href={canonical} />

        <meta id="index__pages-journey__meta__l76" property="og:title" content={journeyTitle} />
        <meta
          id="index__pages-journey__meta__l77"
          property="og:description"
          content={journeyDesc}
        />

        <meta id="index__pages-journey__meta__l78" property="og:url" content={canonical} />

        <meta id="index__pages-journey__meta__l80" name="twitter:title" content={journeyTitle} />
        <meta
          id="index__pages-journey__meta__l81"
          name="twitter:description"
          content={journeyDesc}
        />
      </Helmet>

      {!isFairtradeTemplate && (account?.alternative_color || product?.ai_generated) && (
        <TopDivider id="index__pages-journey__topdivider__l85" bgColor={account.alternative_color}>
          {product?.ai_generated && (
            <>
              <Typography
                id="index__pages-journey__typography__l88"
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: theme.palette.common.white,
                  mt: '2px',
                }}
                textAlign={'center'}
              >
                {t('ai-generated-label')}
              </Typography>
              <FlaskIcon size={14} />
            </>
          )}
        </TopDivider>
      )}
      {isFairtradeTemplate && (
        <LeftDivider
          id="index__pages-journey__leftdivider__l119"
          bgColor={account?.alternative_color || account?.primary_color}
        />
      )}

      {isFairtradeTemplate ? (
        <FairtradeJourneyTemplate
          steps={steps}
          account={account}
          topRef={topRef}
          scrollToRef={ref}
          isImpacts={isImpacts}
          accauntSimpleId={product?.account_simple_id}
          productName={product?.name}
          journeyDescription={journey?.description}
        />
      ) : (
        <PageWrapper>
          <MapProvider>
            <span id="index__pages-journey__span__l106" ref={topRef} />

            <FullWidthSection
              id="index__pages-journey__fullwidthsectionst__l103"
              idInner="index__pages-journey__fullwidthsectioninner__l103"
            >
              <JourneyMap
                steps={steps}
                satelliteView={account.map_satellite_view}
                scrollToRef={ref}
                markerColor={account?.primary_color}
                isTopDivider={!!account?.alternative_color}
                isImpacts={isImpacts}
                accauntId={product?.account_simple_id}
              />
            </FullWidthSection>

            <span id="index__pages-journey__span__l121" ref={ref} />

            <FullWidthSection
              id="index__pages-journey__fullwidthsectionst__l104"
              idInner="index__pages-journey__fullwidthsectioninner__l104"
              sxInner={{ p: '20px' }}
            >
              <JourneyCard steps={steps} journeyName={journey.journey_name} topRef={topRef} />
            </FullWidthSection>
          </MapProvider>
        </PageWrapper>
      )}
      <PageFooter />
      {/* <FooterV2 /> */}
    </LayoutContainer>
  )
}
